import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { Bell, BellOff, Check, AlertTriangle } from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [instantNotifications, setInstantNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (user) {
      setEmailNotifications(user.email_notifications ?? true);
      setInstantNotifications(user.instant_lead_notifications ?? false);
    }
  }, []);

  const updateNotificationSetting = async (key, newValue, revertFn) => {
    setLoading(true);
    setSaved(false);
    try {
      const { error } = await supabase.auth.updateUser({ data: { [key]: newValue } });
      if (error) throw error;
      await supabase.auth.refreshSession();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('❌ Error updating notification settings:', error);
      alert('Erro ao guardar preferências. Tente novamente.');
      revertFn();
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (newValue) => {
    setEmailNotifications(newValue);
    updateNotificationSetting('email_notifications', newValue, () => setEmailNotifications(!newValue));
  };

  const handleInstantToggle = (newValue) => {
    setInstantNotifications(newValue);
    updateNotificationSetting('instant_lead_notifications', newValue, () => setInstantNotifications(!newValue));
  };

  const handleDeleteAccount = async () => {
    const firstConfirm = window.confirm(
      '⚠️ APAGAR CONTA PERMANENTEMENTE?\n\n' +
      'Esta ação é IRREVERSÍVEL e irá:\n\n' +
      '• Apagar todos os seus dados\n' +
      '• Remover o seu método de pagamento\n' +
      '• Apagar todas as suas leads\n' +
      '• Encerrar a sua conta de forma permanente\n\n' +
      'Tem a certeza que deseja continuar?'
    );

    if (!firstConfirm) return;

    const confirmText = prompt(
      'Para confirmar, escreva "APAGAR CONTA" (em maiúsculas) e clique em OK:'
    );

    if (confirmText !== 'APAGAR CONTA') {
      if (confirmText !== null) {
        alert('Texto de confirmação incorreto. Conta não foi apagada.');
      }
      return;
    }

    setDeletingAccount(true);

    try {
      const response = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      alert('✅ Conta apagada com sucesso. Lamentamos vê-lo partir.');
      await logout();
      navigate('/');

    } catch (error) {
      console.error('Error deleting account:', error);
      alert('❌ Erro ao apagar conta: ' + error.message);
      setDeletingAccount(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Configurações</h1>

      {/* Informação da Conta */}
      <div className="bg-white rounded-xl shadow-md p-8 border border-primary/10 space-y-6 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informação da Conta</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
              <input
                type="text"
                value={user?.name || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Área de Atuação</label>
              <input
                type="text"
                value="Contabilidade"
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
          </div>
        </div>

      </div>

      {/* Preferências de Notificações */}
      <div className="bg-white rounded-xl shadow-md p-8 border border-primary/10 mb-6">
        <div className="flex items-center gap-3 mb-6">
          {emailNotifications ? (
            <Bell className="w-6 h-6 text-primary" />
          ) : (
            <BellOff className="w-6 h-6 text-gray-400" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Notificações</h3>
            <p className="text-sm text-gray-600">Gira as suas preferências de notificações</p>
          </div>
        </div>

        {saved && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-800 font-medium">
              Preferências guardadas com sucesso!
            </span>
          </div>
        )}

        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-1">
              <label className="text-sm font-semibold text-gray-900 block mb-1">
                Notificações de Novas Leads
              </label>
              <p className="text-xs text-gray-600">
                Receber email quando há novos pedidos de contacto disponíveis
              </p>
            </div>
            <button
              onClick={() => handleNotificationToggle(!emailNotifications)}
              disabled={loading}
              className={`
                relative inline-flex h-8 w-14 items-center rounded-full transition-colors
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${emailNotifications ? 'bg-primary' : 'bg-gray-300'}
              `}
            >
              <span
                className={`
                  inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                  ${emailNotifications ? 'translate-x-7' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          {!emailNotifications && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-900 font-medium mb-1">
                Notificações Desativadas
              </p>
              <p className="text-xs text-yellow-800">
                Não receberá emails quando houver novos pedidos de contacto disponíveis.
                Terá de verificar a plataforma manualmente.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-1">
              <label className="text-sm font-semibold text-gray-900 block mb-1">
                Notificação Imediata de Nova Lead
              </label>
              <p className="text-xs text-gray-600">
                Receber email no momento em que entra um novo pedido de contacto na plataforma
              </p>
            </div>
            <button
              onClick={() => handleInstantToggle(!instantNotifications)}
              disabled={loading}
              className={`
                relative inline-flex h-8 w-14 items-center rounded-full transition-colors
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${instantNotifications ? 'bg-primary' : 'bg-gray-300'}
              `}
            >
              <span
                className={`
                  inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                  ${instantNotifications ? 'translate-x-7' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Zona de Perigo - Apagar Conta */}
      <div className="bg-white rounded-xl shadow-md p-8 border-2 border-red-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-900">Zona de Perigo</h3>
            <p className="text-sm text-red-700">Ações irreversíveis</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h4 className="text-base font-semibold text-red-900 mb-3">
            Apagar Conta Permanentemente
          </h4>
          <p className="text-sm text-red-800 mb-4">
            Ao apagar a sua conta, todos os seus dados serão removidos permanentemente, incluindo:
          </p>
          <ul className="text-sm text-red-800 space-y-1 mb-6 ml-4">
            <li>• Informação pessoal e de contacto</li>
            <li>• Histórico de leads e pedidos de contacto</li>
            <li>• Método de pagamento associado</li>
            <li>• Preferências e configurações</li>
          </ul>
          <p className="text-sm text-red-900 font-semibold mb-6">
            ⚠️ Esta ação é IRREVERSÍVEL e não pode ser desfeita.
          </p>

          <button
            onClick={handleDeleteAccount}
            disabled={deletingAccount}
            className="w-full py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deletingAccount ? 'A apagar conta...' : 'Apagar a minha conta'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
